import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  ArrowBack,
  Business,
  LocationOn,
  Schedule,
  AttachMoney,
  Code,
  Person,
  Check,
  Timeline,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import TabPanel from '../components/TabPanel';
import { projectsService } from '../services/supabase';
import { ProjectWithSkills } from '../types';
import { validateRequired, validateAndSanitizeInput } from '../utils/validation';
import {
  getStatusColor,
  getStatusText,
  getPriorityColor,
  getPriorityText,
  getAssignmentStatusColor,
  getAssignmentStatusText,
  getYearsText,
  formatDate
} from '../utils/helpers';
import { commonSkills } from '../constants/skills';


const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [project, setProject] = useState<ProjectWithSkills | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    title: '',
    client_name: '',
    description: '',
    budget: 0,
    location: '',
    start_date: '',
    end_date: '',
    status: 'pending' as 'pending' | 'active' | 'completed' | 'cancelled',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    remote_work: false,
    skills: [] as Array<{
      skill_name: string;
      required_years: number;
      is_required: boolean;
    }>
  });

  // Skill input state for editing
  const [skillInput, setSkillInput] = useState({
    skill_name: '',
    required_years: 1,
    is_required: false
  });

  useEffect(() => {
    const fetchProject = async () => {
      if (!id) {
        setError('Project ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await projectsService.getById(parseInt(id));
        
        if (result.error) {
          setError(result.error);
        } else if (result.data) {
          setProject(result.data);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        setError('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  // Initialize edit form when project is loaded or dialog opens
  useEffect(() => {
    if (project && openEditDialog) {
      setEditFormData({
        title: project.title || '',
        client_name: project.client_name || '',
        description: project.description || '',
        budget: project.budget || 0,
        location: project.location || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status,
        priority: project.priority,
        remote_work: project.remote_work,
        skills: project.skills?.map(skill => ({
          skill_name: skill.skill_name,
          required_years: skill.required_years,
          is_required: skill.is_required
        })) || []
      });
    }
  }, [project, openEditDialog]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Project not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/projects')}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }


  // Skill management functions for edit form
  const addSkillToEdit = () => {
    if (!skillInput.skill_name.trim()) {
      setSnackbar({ open: true, message: 'スキル名を入力してください', severity: 'error' });
      return;
    }

    // Check if skill already exists
    if (editFormData.skills.some(skill => skill.skill_name === skillInput.skill_name)) {
      setSnackbar({ open: true, message: 'このスキルは既に追加されています', severity: 'error' });
      return;
    }

    setEditFormData(prev => ({
      ...prev,
      skills: [...prev.skills, { ...skillInput }]
    }));

    // Reset skill input
    setSkillInput({
      skill_name: '',
      required_years: 1,
      is_required: false
    });
  };

  const removeSkillFromEdit = (skillName: string) => {
    setEditFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill.skill_name !== skillName)
    }));
  };

  const handleSaveProject = async () => {
    if (!project) return;

    try {
      // Validate required fields
      const titleValidation = validateRequired(editFormData.title, '案件名');
      if (!titleValidation.isValid) {
        setSnackbar({ open: true, message: titleValidation.errors[0], severity: 'error' });
        return;
      }

      // Validate at least one skill is provided
      if (!editFormData.skills || editFormData.skills.length === 0) {
        setSnackbar({ open: true, message: '少なくとも1つのスキルを追加してください', severity: 'error' });
        return;
      }

      // Sanitize data and handle empty date fields
      const sanitizedData = {
        ...editFormData,
        title: validateAndSanitizeInput(editFormData.title, 200),
        description: editFormData.description ? validateAndSanitizeInput(editFormData.description, 2000) : '',
        client_name: editFormData.client_name ? validateAndSanitizeInput(editFormData.client_name, 200) : '',
        location: editFormData.location ? validateAndSanitizeInput(editFormData.location, 255) : '',
        start_date: editFormData.start_date && editFormData.start_date.trim() !== '' ? editFormData.start_date : undefined,
        end_date: editFormData.end_date && editFormData.end_date.trim() !== '' ? editFormData.end_date : undefined,
        skills: editFormData.skills?.filter(skill => skill.skill_name.trim() !== '') || []
      };

      const result = await projectsService.updateWithSkills(project.id, sanitizedData);
      
      if (result.error) {
        setSnackbar({ open: true, message: `更新に失敗しました: ${result.error}`, severity: 'error' });
      } else {
        setProject(result.data);
        setSnackbar({ open: true, message: '案件を更新しました', severity: 'success' });
        setOpenEditDialog(false);
      }
    } catch (err) {
      setSnackbar({ open: true, message: '更新中にエラーが発生しました', severity: 'error' });
      console.error('Project update error:', err);
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          案件詳細
        </Typography>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => setOpenEditDialog(true)}
          sx={{ bgcolor: '#27ae60' }}
        >
          編集
        </Button>
      </Box>

      {/* 基本情報カード */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 3, alignItems: 'start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {project.title}
                </Typography>
                <Chip
                  label={getPriorityText(project.priority)}
                  size="small"
                  sx={{
                    bgcolor: getPriorityColor(project.priority),
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
              
              {project.client_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <Business fontSize="small" color="action" />
                  <Typography variant="h6" color="textSecondary">
                    {project.client_name}
                  </Typography>
                </Box>
              )}

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {project.description}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
                {project.budget != null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Typography variant="body2">
                      ¥{project.budget.toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {(project.start_date || project.end_date) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2">
                      {project.start_date && formatDate(project.start_date)}
                      {project.start_date && project.end_date && ' - '}
                      {project.end_date && formatDate(project.end_date)}
                    </Typography>
                  </Box>
                )}
                {project.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">
                      {project.location} {project.remote_work && '(リモート可)'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {project.skills?.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={`${skill.skill_name} (${skill.required_years}年)`}
                    size="small"
                    color={skill.is_required ? "primary" : "default"}
                    variant={skill.is_required ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label={getStatusText(project.status)}
                color={getStatusColor(project.status) as any}
                sx={{ fontWeight: 'bold', mb: 2 }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                アサイン数
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#3498db' }}>
                {project.assignments?.length || 0}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* タブメニュー */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
          <Tab label="案件詳細" />
          <Tab label="アサイン一覧" />
          <Tab label="履歴" />
        </Tabs>
      </Card>

      {/* 案件詳細タブ */}
      <TabPanel value={tabValue} index={0} sx={{ py: 3 }}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* 必要スキル・要件 */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                <Code sx={{ mr: 1, verticalAlign: 'middle' }} />
                必要スキル・要件
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {project.skills?.map((skill) => (
                  <Box key={skill.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Check fontSize="small" color={skill.is_required ? "error" : "success"} />
                    <Typography variant="body2" sx={{ fontWeight: skill.is_required ? 'bold' : 'normal' }}>
                      {skill.skill_name} ({skill.required_years}年)
                      {skill.is_required && ' - 必須'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* 詳細情報 */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                詳細情報
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      ステータス
                    </Typography>
                    <Typography variant="body2">{getStatusText(project.status)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      優先度
                    </Typography>
                    <Typography variant="body2">{getPriorityText(project.priority)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      リモートワーク
                    </Typography>
                    <Typography variant="body2">{project.remote_work ? '可能' : '不可'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      作成日
                    </Typography>
                    <Typography variant="body2">{formatDate(project.created_at)}</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* アサイン一覧タブ */}
      <TabPanel value={tabValue} index={1} sx={{ py: 3 }}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {project.assignments && project.assignments.length > 0 ? (
            project.assignments.map((assignment) => (
              <Card key={assignment.id}>
                <CardContent>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, alignItems: 'start' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          エンジニア ID: {assignment.engineer_id}
                        </Typography>
                        <Chip
                          label={getAssignmentStatusText(assignment.status)}
                          color={getAssignmentStatusColor(assignment.status) as any}
                          size="small"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {assignment.role && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2">{assignment.role}</Typography>
                          </Box>
                        )}
                        {assignment.hourly_rate != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney fontSize="small" color="action" />
                            <Typography variant="body2">¥{assignment.hourly_rate.toLocaleString()}/時</Typography>
                          </Box>
                        )}
                      </Box>

                      {(assignment.start_date || assignment.end_date) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2">
                            {assignment.start_date && formatDate(assignment.start_date)}
                            {assignment.start_date && assignment.end_date && ' - '}
                            {assignment.end_date && formatDate(assignment.end_date)}
                          </Typography>
                        </Box>
                      )}

                      {assignment.notes && (
                        <>
                          <Typography variant="body2" sx={{ color: 'textSecondary', mb: 1 }}>
                            メモ:
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            {assignment.notes}
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                        作成日: {formatDate(assignment.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  まだアサインされたエンジニアはいません
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </TabPanel>

      {/* 履歴タブ */}
      <TabPanel value={tabValue} index={2} sx={{ py: 3 }}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                案件履歴
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">案件作成</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(project.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">最終更新</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(project.updated_at)}
                  </Typography>
                </Box>
                {project.assignments && project.assignments.length > 0 && (
                  project.assignments.map((assignment) => (
                    <Box key={assignment.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: '#e8f5e8' }}>
                      <Typography variant="body2">
                        エンジニアアサイン (ID: {assignment.engineer_id}) - {getAssignmentStatusText(assignment.status)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(assignment.created_at)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>


      {/* 編集ダイアログ */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>案件情報編集</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <TextField 
              fullWidth 
              label="案件名" 
              required
              value={editFormData.title}
              onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="クライアント名" 
              value={editFormData.client_name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, client_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="案件詳細"
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                fullWidth 
                label="予算（円）" 
                type="number" 
                value={editFormData.budget || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, budget: parseInt(e.target.value) || 0 }))}
              />
              <TextField 
                fullWidth 
                label="勤務地" 
                value={editFormData.location}
                onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
              />
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                fullWidth 
                label="開始日" 
                type="date" 
                value={editFormData.start_date}
                onChange={(e) => setEditFormData(prev => ({ ...prev, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField 
                fullWidth 
                label="終了日" 
                type="date" 
                value={editFormData.end_date}
                onChange={(e) => setEditFormData(prev => ({ ...prev, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select 
                  label="ステータス" 
                  value={editFormData.status}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value as any }))}
                >
                  <MenuItem value="pending">準備中</MenuItem>
                  <MenuItem value="active">募集中</MenuItem>
                  <MenuItem value="completed">完了</MenuItem>
                  <MenuItem value="cancelled">キャンセル</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>優先度</InputLabel>
                <Select 
                  label="優先度" 
                  value={editFormData.priority}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  <MenuItem value="urgent">緊急</MenuItem>
                  <MenuItem value="high">高</MenuItem>
                  <MenuItem value="medium">中</MenuItem>
                  <MenuItem value="low">低</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* リモートワーク設定 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.remote_work}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, remote_work: e.target.checked }))}
                />
              }
              label="リモートワーク可能"
            />

            {/* 必要スキル・要件 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <Code sx={{ mr: 1 }} />
                必要スキル・要件
              </Typography>
              
              {/* スキル追加フォーム */}
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Autocomplete
                    freeSolo
                    options={commonSkills}
                    value={skillInput.skill_name}
                    onInputChange={(event, newValue) => {
                      setSkillInput(prev => ({ ...prev, skill_name: newValue }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="スキル名"
                        placeholder="スキル名を入力してください"
                        fullWidth
                      />
                    )}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 2, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      label="要求年数"
                      type="number"
                      value={skillInput.required_years}
                      onChange={(e) => setSkillInput(prev => ({ 
                        ...prev, 
                        required_years: parseInt(e.target.value) || 1
                      }))}
                      inputProps={{ min: 0, max: 20 }}
                      helperText="年"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={skillInput.is_required}
                          onChange={(e) => setSkillInput(prev => ({ ...prev, is_required: e.target.checked }))}
                        />
                      }
                      label="必須スキル"
                    />
                    <Button 
                      variant="contained" 
                      onClick={addSkillToEdit}
                      disabled={!skillInput.skill_name.trim()}
                    >
                      追加
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* 追加されたスキル一覧 */}
              {editFormData.skills.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                    追加されたスキル ({editFormData.skills.length}件)
                  </Typography>
                  <List dense>
                    {editFormData.skills.map((skill, index) => (
                      <ListItem key={index} sx={{ px: 1, py: 0.5, bgcolor: '#f5f5f5', mb: 0.5, borderRadius: 1 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {skill.skill_name}
                              </Typography>
                              <Chip
                                label={getYearsText(skill.required_years)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {skill.is_required && (
                                <Chip
                                  label="必須"
                                  size="small"
                                  color="error"
                                  variant="filled"
                                />
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeSkillFromEdit(skill.skill_name)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveProject}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDetail;